/**
 * Error Boundary Component for GameFi Feature
 * Catches and displays errors gracefully
 * @module gamefi/components/ErrorBoundary
 */

import { Result, Button } from 'antd'
import React, { Component, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary component to catch React errors in GameFi feature
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[GameFi ErrorBoundary] Caught error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div style={{ padding: '50px' }}>
          <Result
            status="error"
            title="Đã xảy ra lỗi"
            subTitle="Xin lỗi, có lỗi không mong muốn xảy ra. Vui lòng thử lại hoặc liên hệ bộ phận hỗ trợ."
            extra={[
              <Button type="primary" key="reload" onClick={this.handleReset}>
                Tải lại trang
              </Button>,
              <Button key="back" onClick={() => window.history.back()}>
                Quay lại
              </Button>,
            ]}
          >
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div
                style={{
                  textAlign: 'left',
                  padding: '16px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  marginTop: '16px',
                }}
              >
                <strong>Error Details (Development Only):</strong>
                <pre style={{ marginTop: '8px', fontSize: '12px' }}>
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </div>
            )}
          </Result>
        </div>
      )
    }

    return this.props.children
  }
}
